import {
  Column,
  Columns,
  Divider,
  Heading,
  Stack,
  Text,
} from "@origin-digital/ods-core";
import { useFormikContext } from "formik";
import * as React from "react";
import { AccountPayer, FormValues } from "./types";
import { capitalizeFirstChar, generateBillingAddressString } from "./helper";
import ReadOnlyUploadedFile from "./FormComponents/ReadOnlyUploadedFile";
import ReadOnlyFieldWrapper from "./ReadOnlyFieldWrapper";
import { format } from "date-fns";
import FormContext from "../../context/FormContext";
import { usePermissions } from "src/hooks/usePermissions";

const DATE_FORMAT = "dd/MM/yyyy";

const ReadOnlyAccountBlock = () => {
  const { values } = useFormikContext<FormValues>();
  const { formState } = React.useContext(FormContext);
  const { isHomeUser } = usePermissions();

  const handleDBFormName = (
    accountPrimaryName: string,
    electricityRequesterName: string | undefined
  ) => {
    if (formState.electricityService?.channel === "NETWORK") {
      return accountPrimaryName === "Not provided" && electricityRequesterName
        ? electricityRequesterName
        : accountPrimaryName;
    }
    return accountPrimaryName;
  };
  const accountPayersForInternet = React.useMemo(() => {
    const accountPayers = [
      ...(formState.electricityService ? [values.electricityAccountPayer] : []),
      ...(formState.gasServices?.gasLine ? [values.gasLine?.accountPayer] : []),
      ...(formState.gasServices?.gasMeter
        ? [values.gasMeter?.accountPayer]
        : []),
      ...(formState.gasServices?.both ? [values.both?.accountPayer] : []),
    ];
    return accountPayers;
  }, [
    values.electricityAccountPayer,
    values.gasLine?.accountPayer,
    values.gasMeter?.accountPayer,
    values.both?.accountPayer,
  ]);
  // If and if ONLY Internet service is added, account should show email, first & last name.
  // Otherwise all fields present in the account block will be displayed
  const isInternetOnlyCustomer =
    (values.internetAccountPayer === "SOMEONE_ELSE" &&
      accountPayersForInternet.includes(AccountPayer.SOMEONE_ELSE) === false) ||
    (formState.internetService &&
      values.internetAccountPayer === AccountPayer.COMPANY &&
      isHomeUser &&
      accountPayersForInternet.length === 0);
  const DOBAndPhone = () => {
    const showSection =
      values.accountPrimaryDob ||
      formState.electricityService?.channel === "NETWORK" ||
      values.accountPrimaryPhone;
    if (!showSection) {
      return null;
    }
    return isInternetOnlyCustomer ? null : (
      <>
        <Columns
          collapseBelow="md"
          space={{
            xs: "xlarge",
            md: "medium",
          }}
        >
          <Column>
            {/* Date of birth */}
            <ReadOnlyFieldWrapper
              value={
                values.accountPrimaryDob ||
                formState.electricityService?.channel === "NETWORK"
              }
            >
              <Stack space="xsmall">
                <Stack space="xxsmall">
                  <Text variant="body-small" tone="neutralLight">
                    Date of birth
                  </Text>
                  <Text>
                    {values.accountPrimaryDob
                      ? format(new Date(values.accountPrimaryDob), DATE_FORMAT)
                      : formState.electricityService?.channel === "NETWORK"
                      ? "Not Provided"
                      : ""}
                  </Text>
                </Stack>
                <Divider />
              </Stack>
            </ReadOnlyFieldWrapper>
            {/* End Date of birth */}
          </Column>
          <Column>
            {/* Phone */}
            <ReadOnlyFieldWrapper value={values.accountPrimaryPhone}>
              <Stack space="xsmall">
                <Stack space="xxsmall">
                  <Text variant="body-small" tone="neutralLight">
                    Phone
                  </Text>
                  <Text>{values.accountPrimaryPhone}</Text>
                </Stack>
                <Divider />
              </Stack>
            </ReadOnlyFieldWrapper>
            {/* End Phone */}
          </Column>
        </Columns>
      </>
    );
  };
  const AccountHolder = () => {
    return isInternetOnlyCustomer ? null : (
      <Columns
        collapseBelow="md"
        space={{
          xs: "none",
          md: "xlarge",
        }}
        alignY="stretch"
      >
        {/* Fields */}
        <Column width="2/3">
          <Stack space="xlarge">
            {/* Account holder */}
            <Stack space="xsmall">
              <Stack space="xxsmall">
                <Text variant="body-small" tone="neutralLight">
                  Account holder
                </Text>
                <Text>
                  {values.accountHolder === "person" ? `Personal` : `Business`}
                </Text>
              </Stack>
              <Divider />
            </Stack>
            {/* End Account holder */}

            {/* Company name */}
            {values.accountHolder === "company" ? (
              <ReadOnlyFieldWrapper value={values.companyName}>
                <Stack space="xsmall">
                  <Stack space="xxsmall">
                    <Text variant="body-small" tone="neutralLight">
                      Company name
                    </Text>
                    <Text>{values.companyName}</Text>
                  </Stack>
                  <Divider />
                </Stack>
              </ReadOnlyFieldWrapper>
            ) : null}
            {/* End Company name */}

            {/* ABN */}
            {values.accountHolder === "company" ? (
              <ReadOnlyFieldWrapper value={values.abn}>
                <Columns
                  collapseBelow="md"
                  space={{
                    xs: "none",
                    md: "medium",
                  }}
                >
                  <Column>
                    <Stack space="xsmall">
                      <Stack space="xxsmall">
                        <Text variant="body-small" tone="neutralLight">
                          ABN
                        </Text>
                        <Text>{values.abn}</Text>
                      </Stack>
                      <Divider />
                    </Stack>
                  </Column>
                  <Column />
                </Columns>
              </ReadOnlyFieldWrapper>
            ) : null}
            {/* End ABN */}
          </Stack>
        </Column>
        {/* End Fields */}
        {/* HelperText */}
        <Column width="1/3" />
        {/* End HelperText */}
      </Columns>
    );
  };
  const FirstLastName = () => {
    return (
      <>
        {" "}
        <Column>
          {/* First name */}
          <ReadOnlyFieldWrapper value={values.accountPrimaryFirstName}>
            <Stack space="xsmall">
              <Stack space="xxsmall">
                <Text variant="body-small" tone="neutralLight">
                  First name
                </Text>
                <Text>
                  {handleDBFormName(
                    values.accountPrimaryFirstName,
                    values.electricityRequesterFirstName
                  )}
                </Text>
              </Stack>
              <Divider />
            </Stack>
          </ReadOnlyFieldWrapper>
          {/* End First name */}
        </Column>
        <Column>
          {/* Last name */}
          <ReadOnlyFieldWrapper value={values.accountPrimaryLastName}>
            <Stack space="xsmall">
              <Stack space="xxsmall">
                <Text variant="body-small" tone="neutralLight">
                  Last name
                </Text>
                <Text>
                  {handleDBFormName(
                    values.accountPrimaryLastName,
                    values.electricityRequesterLastName
                  )}
                </Text>
              </Stack>
              <Divider />
            </Stack>
          </ReadOnlyFieldWrapper>
          {/* End Last name */}
        </Column>
      </>
    );
  };
  const CommsAndBillingAddress = () => {
    return isInternetOnlyCustomer ? null : (
      <Stack space="xlarge">
        {/* Comms prefs */}
        <ReadOnlyFieldWrapper value={values.accountCommsPrefs}>
          <Stack space="xsmall">
            <Stack space="xxsmall">
              <Text variant="body-small" tone="neutralLight">
                Send communications, including bills, by:
              </Text>
              <Text>{capitalizeFirstChar(values.accountCommsPrefs)}</Text>
            </Stack>
            <Divider />
          </Stack>
        </ReadOnlyFieldWrapper>
        {/* End Comms prefs */}
        {/* Billing address */}
        <ReadOnlyFieldWrapper
          value={generateBillingAddressString(values["billing-address"])}
        >
          <Stack space="xsmall">
            <Stack space="xxsmall">
              <Text variant="body-small" tone="neutralLight">
                Billing address
              </Text>
              <Text>
                {generateBillingAddressString(values["billing-address"])}
              </Text>
            </Stack>
            <Divider />
          </Stack>
        </ReadOnlyFieldWrapper>
      </Stack>
    );
  };
  return (
    <Stack
      space={{
        xs: "xxlarge",
        md: "xxxlarge",
      }}
    >
      {/* Form container */}
      <Stack
        space={{
          xs: "xlarge",
          md: "xlarge",
        }}
      >
        {/* Account holder */}
        <AccountHolder />
        {/* End Account holder */}

        {/* Name, DOB, Phone, Email, Comms prefs + Billing address */}
        <Columns
          collapseBelow="md"
          space={{
            xs: "none",
            md: "xlarge",
          }}
          alignY="stretch"
        >
          {/* Fields */}
          <Column width="2/3">
            <Stack
              space={{
                xs: "xlarge",
                md: "xlarge",
              }}
            >
              {/* First + last name */}
              <Columns
                collapseBelow="md"
                space={{
                  xs: "xlarge",
                  md: "medium",
                }}
              >
                <FirstLastName />
              </Columns>
              {/* End First + last name */}

              {/* DOB + Phone */}
              <DOBAndPhone />
              {/* End DOB + Phone */}

              {/* Email */}
              <ReadOnlyFieldWrapper value={values.accountPrimaryEmail}>
                <Stack space="xsmall">
                  <Stack space="xxsmall">
                    <Text variant="body-small" tone="neutralLight">
                      Email
                    </Text>
                    <Text>{values.accountPrimaryEmail}</Text>
                  </Stack>
                  <Divider />
                </Stack>
              </ReadOnlyFieldWrapper>
              {/* End Email */}

              {/* Comms prefs*/}
              <CommsAndBillingAddress />
            </Stack>
            {/* End Billing address */}
          </Column>
          {/* End Fields */}
          {/* HelperText */}
          <Column width="1/3" />
          {/* End HelperText */}
        </Columns>
        {/* End Name, DOB, Phone, Email, Comms prefs + Billing address */}

        {/* Empty stack hack in lieu of <Divider /> to maintain spacing */}
        {!isInternetOnlyCustomer &&
          (values.secondaryAccountHolder ? (
            <Columns
              collapseBelow="md"
              space={{
                xs: "none",
                md: "xlarge",
              }}
              alignY="stretch"
            >
              {/* Fields */}
              <Column width="2/3">
                <Stack
                  space={{
                    xs: "xlarge",
                    md: "xlarge",
                  }}
                >
                  <Heading variant="h4">Secondary account holder</Heading>
                  {/* First + last name */}
                  <Columns
                    collapseBelow="md"
                    space={{
                      xs: "xlarge",
                      md: "medium",
                    }}
                  >
                    <Column>
                      {/* First name */}
                      <ReadOnlyFieldWrapper
                        value={values.accountSecondaryFirstName}
                      >
                        <Stack space="xsmall">
                          <Stack space="xxsmall">
                            <Text variant="body-small" tone="neutralLight">
                              First name
                            </Text>
                            <Text>{values.accountSecondaryFirstName}</Text>
                          </Stack>
                          <Divider />
                        </Stack>
                      </ReadOnlyFieldWrapper>
                      {/* End First name */}
                    </Column>
                    <Column>
                      {/* Last name */}
                      <ReadOnlyFieldWrapper
                        value={values.accountSecondaryLastName}
                      >
                        <Stack space="xsmall">
                          <Stack space="xxsmall">
                            <Text variant="body-small" tone="neutralLight">
                              Last name
                            </Text>
                            <Text>{values.accountSecondaryLastName}</Text>
                          </Stack>
                          <Divider />
                        </Stack>
                      </ReadOnlyFieldWrapper>
                      {/* End Last name */}
                    </Column>
                  </Columns>
                  {/* End First + last name */}
                  {/* DOB + Phone */}
                  <Columns
                    collapseBelow="md"
                    space={{
                      xs: "xlarge",
                      md: "medium",
                    }}
                  >
                    <Column>
                      {/* Date of birth */}
                      <ReadOnlyFieldWrapper value={values.accountSecondaryDob}>
                        <Stack space="xsmall">
                          <Stack space="xxsmall">
                            <Text variant="body-small" tone="neutralLight">
                              Date of birth
                            </Text>
                            <Text>
                              {format(
                                new Date(values.accountSecondaryDob || ""),
                                DATE_FORMAT
                              )}
                            </Text>
                          </Stack>
                          <Divider />
                        </Stack>
                      </ReadOnlyFieldWrapper>
                      {/* End Date of birth */}
                    </Column>
                    <Column>
                      {/* Phone */}
                      <ReadOnlyFieldWrapper
                        value={values.accountSecondaryPhone}
                      >
                        <Stack space="xsmall">
                          <Stack space="xxsmall">
                            <Text variant="body-small" tone="neutralLight">
                              Phone
                            </Text>
                            <Text>{values.accountSecondaryPhone}</Text>
                          </Stack>
                          <Divider />
                        </Stack>
                      </ReadOnlyFieldWrapper>
                      {/* End Phone */}
                    </Column>
                  </Columns>
                  {/* End DOB + Phone */}
                  {/* Email */}
                  <ReadOnlyFieldWrapper value={values.accountSecondaryEmail}>
                    <Stack space="xsmall">
                      <Stack space="xxsmall">
                        <Text variant="body-small" tone="neutralLight">
                          Email
                        </Text>
                        <Text>{values.accountSecondaryEmail}</Text>
                      </Stack>
                      <Divider />
                    </Stack>
                  </ReadOnlyFieldWrapper>
                  {/* End Email */}
                  {/* Add secondary name to bill */}
                  {values.addSecondaryNameToBill && (
                    <Stack space="xsmall">
                      <Stack space="xxsmall">
                        <Text variant="body-small" tone="neutralLight">
                          Add secondary account holder's name to Origin bills
                        </Text>
                        <Text>Yes</Text>
                      </Stack>
                      <Divider />
                    </Stack>
                  )}
                  {/* End Add secondary name to bill */}
                </Stack>
              </Column>
              {/* End Fields */}
              {/* HelperText */}
              <Column width="1/3" />
              {/* End HelperText */}
            </Columns>
          ) : null)}
        {/* End Secondary account holder */}

        {/* Empty stack hack in lieu of <Divider /> to maintain spacing */}
        {/* <Stack /> */}

        {/* Customer consent */}
        {!isInternetOnlyCustomer && (
          <Columns
            collapseBelow="md"
            space={{
              xs: "none",
              md: "xlarge",
            }}
            alignY="stretch"
          >
            {/* Fields */}
            <Column width="2/3">
              <Stack
                space={{
                  xs: "large",
                  md: "xlarge",
                }}
              >
                {values.customerConsentFiles &&
                  values.customerConsentFiles.length > 0 && (
                    <Stack space="xxsmall">
                      {/*<Text variant="body-small" tone="neutralLight">Customer consent</Text>*/}
                      {values.customerConsentFiles?.map((attachment) => (
                        <ReadOnlyUploadedFile
                          key={attachment.attachmentId}
                          attachment={attachment}
                        />
                      ))}
                    </Stack>
                  )}
              </Stack>
            </Column>
            {/* Fields */}
            {/* HelperText */}
            <Column width="1/3" />
            {/* End HelperText */}
          </Columns>
        )}
        {/* End Customer consent */}
      </Stack>
      {/* End Form container */}
    </Stack>
  );
};

export default ReadOnlyAccountBlock;
